import React, { useState, useRef, useCallback } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { useDriveSubFolders, useDriveFiles } from '@/hooks/useDocFlow';
import { DRIVE_ROOT_FOLDER_ID } from '@/types/docflow';
import type { IDriveFile } from '@/types/docflow';

export interface SelectedFile {
  source: 'drive' | 'device';
  driveFile?: IDriveFile;
  localFile?: File;
  name: string;
}

interface PartCreateFilePickerProps {
  onSelect: (file: SelectedFile) => void;
  onCancel: () => void;
}

/** Inline Drive browser — embedded in the file picker, not a separate dialog */
const InlineDriveBrowser: React.FC<{
  onSelectFile: (file: IDriveFile) => void;
  selectedFileId?: string;
}> = ({ onSelectFile, selectedFileId }) => {
  const [currentFolderId, setCurrentFolderId] = useState(DRIVE_ROOT_FOLDER_ID);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: string; name: string }[]>([
    { id: DRIVE_ROOT_FOLDER_ID, name: 'Root' },
  ]);

  const { data: folders, isLoading: foldersLoading } = useDriveSubFolders(currentFolderId);
  const { data: files, isLoading: filesLoading } = useDriveFiles(currentFolderId);

  const navigateToFolder = useCallback((folderId: string, folderName: string) => {
    setCurrentFolderId(folderId);
    setBreadcrumbs(prev => [...prev, { id: folderId, name: folderName }]);
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    const crumb = breadcrumbs[index];
    setCurrentFolderId(crumb.id);
    setBreadcrumbs(prev => prev.slice(0, index + 1));
  }, [breadcrumbs]);

  const isLoading = foldersLoading || filesLoading;

  // Filter to PDF/image files only
  const filteredFiles = (files || []).filter(f => {
    const mime = f.mime_type || '';
    const name = f.name.toLowerCase();
    return mime === 'application/pdf' || mime.startsWith('image/') ||
      name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg');
  });

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-1 px-3 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 text-xs overflow-x-auto">
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.id} className="flex items-center gap-1 whitespace-nowrap">
            {i > 0 && <ChevronRight className="w-3 h-3 text-gray-400" />}
            <button
              onClick={() => navigateToBreadcrumb(i)}
              className={`hover:text-blue-600 dark:hover:text-blue-400 ${
                i === breadcrumbs.length - 1
                  ? 'text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {crumb.name}
            </button>
          </span>
        ))}
      </div>

      {/* Content */}
      <div className="max-h-64 overflow-y-auto">
        {isLoading ? (
          <div className="text-sm text-gray-400 text-center py-6">Loading...</div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {/* Folders */}
            {(folders || []).map(folder => (
              <button
                key={folder.id}
                onClick={() => navigateToFolder(folder.id, folder.name)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
              >
                <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <span className="truncate text-gray-700 dark:text-gray-200">{folder.name}</span>
                <ChevronRight className="w-3 h-3 text-gray-400 ml-auto shrink-0" />
              </button>
            ))}

            {/* Files */}
            {filteredFiles.map(file => {
              const isSelected = selectedFileId === file.id;
              return (
                <button
                  key={file.id}
                  onClick={() => onSelectFile(file)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left ${
                    isSelected
                      ? 'bg-blue-50 dark:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <svg className={`w-4 h-4 shrink-0 ${isSelected ? 'text-blue-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className={`truncate ${isSelected ? 'text-blue-700 dark:text-blue-300 font-medium' : 'text-gray-700 dark:text-gray-200'}`}>
                    {file.name}
                  </span>
                  {file.size !== undefined && (
                    <span className="text-xs text-gray-400 ml-auto shrink-0">
                      {formatFileSize(file.size)}
                    </span>
                  )}
                </button>
              );
            })}

            {/* Empty state */}
            {(folders || []).length === 0 && filteredFiles.length === 0 && (
              <div className="text-sm text-gray-400 text-center py-6">
                No PDF or image files in this folder
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PartCreateFilePicker: React.FC<PartCreateFilePickerProps> = ({ onSelect, onCancel }) => {
  const [source, setSource] = useState<'drive' | 'device'>('drive');
  const [selectedFile, setSelectedFile] = useState<SelectedFile | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDriveSelect = (file: IDriveFile) => {
    setSelectedFile({ source: 'drive', driveFile: file, name: file.name });
  };

  const handleDeviceFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile({ source: 'device', localFile: file, name: file.name });
    }
  };

  const handleNext = () => {
    if (selectedFile) {
      onSelect(selectedFile);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full h-[90vh] overflow-hidden m-4 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Select Document
          </h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Source toggle */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Source</label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
              <button
                onClick={() => { setSource('drive'); setSelectedFile(null); }}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm ${
                  source === 'drive'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.71 3.5L1.15 15l3.43 5.97L11.14 9.47 7.71 3.5zm1.14 0h6.86l6.58 11.5h-6.86L8.85 3.5zM19.57 15H5.71l-3.43 6h13.86l3.43-6z" />
                </svg>
                Google Drive
              </button>
              <button
                onClick={() => { setSource('device'); setSelectedFile(null); }}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm border-l border-gray-300 dark:border-gray-600 ${
                  source === 'device'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload
              </button>
            </div>
          </div>

          {/* Source-specific content */}
          {source === 'drive' ? (
            <InlineDriveBrowser
              onSelectFile={handleDriveSelect}
              selectedFileId={selectedFile?.driveFile?.id}
            />
          ) : (
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp"
                onChange={handleDeviceFile}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-4 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 dark:hover:border-blue-500 transition-colors text-center"
              >
                {selectedFile ? (
                  <div>
                    <svg className="w-8 h-8 mx-auto text-blue-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{selectedFile.name}</p>
                    {selectedFile.localFile && (
                      <p className="text-xs text-gray-400 mt-1">
                        {formatFileSize(selectedFile.localFile.size)}
                      </p>
                    )}
                    <p className="text-xs text-blue-500 mt-1">Click to change</p>
                  </div>
                ) : (
                  <div>
                    <svg className="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Choose file from device</p>
                    <p className="text-xs text-gray-400 mt-1">PDF, PNG, JPG accepted</p>
                  </div>
                )}
              </button>
            </div>
          )}

          {/* Selected file indicator (for Drive) */}
          {source === 'drive' && selectedFile && (
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <svg className="w-4 h-4 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-blue-700 dark:text-blue-300 truncate">{selectedFile.name}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleNext}
            disabled={!selectedFile}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default PartCreateFilePicker;
